import React from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

/**
 * Reusable Modern Common Pagination Component
 * Used platform-wide for consistent, high-contrast pagination controls.
 * Only renders when total record count is greater than limit (default > 25).
 *
 * @param {number} page - Current page (1-indexed)
 * @param {number} limit - Items per page (default: 25)
 * @param {number} total - Total records count from API
 * @param {function} onPageChange - Callback when page changes (newPage: number)
 * @param {function} onLimitChange - Optional callback when page size changes
 */
export default function CommonPagination({
  page = 1,
  limit = 25,
  total = 0,
  onPageChange,
  onLimitChange,
  limitOptions = [25, 50, 100],
  className = "",
}) {
  // Only render pagination when there are total records
  if (!total || total <= 0) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, total);

  // Smart page numbers calculation with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div
      className={`pt-5 mt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-700 select-none ${className}`}
    >
      {/* Left: Modern Record Counter & Optional Limit Selector */}
      <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
        <div className="flex items-center gap-1.5 text-slate-600 font-medium">
          <span>Showing</span>
          <span className="font-extrabold text-slate-900 bg-slate-100 px-2.5 py-0.5 rounded-lg border border-slate-200/80 shadow-xs">
            {startRecord}–{endRecord}
          </span>
          <span>of</span>
          <span className="font-extrabold text-slate-900">{total}</span>
          <span>results</span>
        </div>

        {onLimitChange && (
          <div className="flex items-center gap-1.5 pl-2 sm:border-l border-slate-200">
            <span className="text-slate-500 font-medium">Per page:</span>
            <select
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 outline-none focus:border-[#115B9D] focus:ring-1 focus:ring-[#115B9D]/30 transition-all cursor-pointer shadow-xs"
            >
              {limitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: Modern Page Controls */}
      <div className="inline-flex items-center gap-1.5 bg-slate-50/80 p-1 rounded-2xl border border-slate-200/70 shadow-xs">
        {/* Previous Button */}
        <button
          type="button"
          onClick={() => onPageChange && onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-transparent hover:border-slate-200 bg-transparent hover:bg-white text-slate-700 font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          title="Previous page"
        >
          <FiChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline text-xs">Prev</span>
        </button>

        {/* Numeric Page Buttons */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((p, idx) =>
            p === "..." ? (
              <span
                key={`dots-${idx}`}
                className="w-7 text-center text-slate-400 font-black select-none"
              >
                ...
              </span>
            ) : (
              <button
                key={`page-${p}`}
                type="button"
                onClick={() => onPageChange && onPageChange(Number(p))}
                className={`min-w-[32px] h-8 px-2 rounded-xl text-xs font-extrabold transition-all cursor-pointer flex items-center justify-center ${
                  currentPage === p
                    ? "bg-[#115B9D] text-white shadow-md shadow-[#115B9D]/30 font-black"
                    : "text-slate-700 hover:text-slate-900 hover:bg-white hover:shadow-xs"
                }`}
              >
                {p}
              </button>
            )
          )}
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={() => onPageChange && onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-transparent hover:border-slate-200 bg-transparent hover:bg-white text-slate-700 font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          title="Next page"
        >
          <span className="hidden sm:inline text-xs">Next</span>
          <FiChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
